/**
 * Export: CanonicalStore -> v2 cross-platform backup JSON (strict writer, §0.5).
 *
 *  - Emit required keys + all optional arrays; appointments always [].
 *  - userProfile ALWAYS null (never overwrite a receiving device's profile, never
 *    leak anything). AI keys are therefore never emitted.
 *  - Single-currency file: sourceCurrency = the store's display currency; every
 *    amount is already in it.
 *  - Dates as ISO-8601 whole seconds UTC. Fuel/charge mirror spendings are NOT
 *    emitted (importers rebuild them from fuelLogs) — the stored set is manual-only.
 */
import type {
  CanonicalStore,
  Car,
  FuelLog,
  Spending,
  Tyre,
  TyreSet,
  MountInterval,
  Trip,
  MaintenanceReminder,
  PreferredService,
  TankEstimate,
  Epoch,
} from "../model.ts";
import { formatTimestamp, nowEpochSeconds } from "./timestamps.ts";

type Json = Record<string, unknown>;

/** Drop undefined and null entries; keep arrays (even empty) and false/0. */
function clean(obj: Json): Json {
  const out: Json = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null) continue;
    out[k] = v;
  }
  return out;
}

const ts = (e: Epoch): string | undefined => formatTimestamp(e) ?? undefined;

export interface SerializeOptions {
  /** Override the export timestamp (tests). Defaults to now. */
  exportedAt?: number;
}

export function serializeBackup(store: CanonicalStore, opts: SerializeOptions = {}): unknown {
  const currency = store.profile.displayCurrency;
  return {
    version: 2,
    exportedAt: formatTimestamp(opts.exportedAt ?? nowEpochSeconds()),
    sourceCurrency: currency,
    userProfile: null,
    cars: store.cars.map(serCar),
    fuelLogs: store.fuelLogs.map((l) => serFuelLog(l, currency)),
    spendings: store.spendings.map((s) => serSpending(s, currency)),
    tyres: store.tyres.map(serTyre),
    tyreSets: store.tyreSets.map(serTyreSet),
    appointments: [],
    trips: store.trips.map(serTrip),
    maintenanceReminders: store.maintenanceReminders.map(serReminder),
    services: store.services.map(serService),
  };
}

export function serializeToText(store: CanonicalStore, opts: SerializeOptions = {}): string {
  return JSON.stringify(serializeBackup(store, opts), null, 2);
}

function serTankEstimate(e: TankEstimate | null | undefined): Json | undefined {
  if (!e) return undefined;
  return clean({
    fullRange: e.fullRange,
    rangeLeft: e.rangeLeft,
    percent: e.percent,
    volume: e.volume,
    savedAt: ts(e.savedAt ?? null),
  });
}

function serCar(c: Car): Json {
  return clean({
    id: c.id,
    make: c.make,
    model: c.model,
    year: c.year,
    trim: c.trim,
    licensePlate: c.licensePlate,
    vin: c.vin,
    color: c.color,
    engineType: c.engineType,
    fuelType: c.fuelType,
    manufacturerAvgConsumption: c.manufacturerAvgConsumption,
    manufacturerAvgEnergy: c.manufacturerAvgEnergy,
    batteryCapacity: c.batteryCapacity,
    tankVolume: c.tankVolume,
    tankEstimate: serTankEstimate(c.tankEstimate),
    chargeEstimate: serTankEstimate(c.chargeEstimate),
    purchasePrice: c.purchasePrice,
    purchaseCurrency: c.purchaseCurrency,
    purchaseDate: ts(c.purchaseDate ?? null),
    purchaseOdometer: c.purchaseOdometer,
    insuranceExpiry: ts(c.insuranceExpiry ?? null),
    insuranceAmount: c.insuranceAmount,
    insuranceDocumentFilename: c.insuranceDocumentFilename,
    roadTaxExpiry: ts(c.roadTaxExpiry ?? null),
    roadTaxAmount: c.roadTaxAmount,
    roadTaxDocumentFilename: c.roadTaxDocumentFilename,
    photoFilenames: c.photoFilenames ?? [],
    bluetoothDeviceAddress: c.bluetoothDeviceAddress,
    autoTripRecording: c.autoTripRecording ?? false,
    vehicleType: c.vehicleType ?? "car",
    createdAt: ts(c.createdAt ?? null),
  });
}

function serFuelLog(l: FuelLog, currency: string): Json {
  return clean({
    id: l.id,
    carId: l.carId,
    date: ts(l.date ?? null),
    odometer: l.odometer,
    volumeAdded: l.volumeAdded,
    kwhAdded: l.kwhAdded,
    solarKwh: l.solarKwh,
    solarPrice: l.solarPrice,
    totalCost: l.totalCost,
    fuelCost: l.fuelCost,
    energyCost: l.energyCost,
    currency,
    fuelGrade: l.fuelGrade ?? "Regular 95",
    isFullFill: l.isFullFill ?? true,
    stationName: l.stationName,
    notes: l.notes,
    avgSpeed: l.avgSpeed,
    photoFilenames: l.photoFilenames ?? [],
    documentFilenames: l.documentFilenames ?? [],
  });
}

function serSpending(s: Spending, currency: string): Json {
  return clean({
    id: s.id,
    carId: s.carId,
    amount: s.amount,
    category: s.category ?? "Other",
    date: ts(s.date ?? null),
    notes: s.notes,
    currency,
    // fuelLogId intentionally omitted — stored set is manual only.
    photoFilenames: s.photoFilenames ?? [],
    documentFilenames: s.documentFilenames ?? [],
  });
}

function serTyre(t: Tyre): Json {
  return clean({
    id: t.id,
    carId: t.carId,
    position: t.position ?? "Front Left",
    season: t.season ?? "All Seasons",
    brand: t.brand,
    model: t.model,
    size: t.size,
    purchaseDate: ts(t.purchaseDate ?? null),
    purchasePrice: t.purchasePrice,
    purchaseOdometer: t.purchaseOdometer,
    installDate: ts(t.installDate ?? null),
    installOdometer: t.installOdometer,
    treadDepth: t.treadDepth,
    pressure: t.pressure,
    dotCode: t.dotCode,
    notes: t.notes,
    photoFilenames: t.photoFilenames ?? [],
    documentFilenames: t.documentFilenames ?? [],
    spendingId: t.spendingId,
    createdAt: ts(t.createdAt ?? null),
  });
}

function serMount(m: MountInterval): Json {
  return clean({
    id: m.id,
    mountedOdometer: m.mountedOdometer,
    mountedDate: ts(m.mountedDate ?? null),
    removedOdometer: m.removedOdometer,
    removedDate: ts(m.removedDate ?? null),
  });
}

function serTyreSet(t: TyreSet): Json {
  return clean({
    id: t.id,
    carId: t.carId,
    name: t.name,
    season: t.season ?? "All Seasons",
    brand: t.brand,
    model: t.model,
    size: t.size,
    purchasePrice: t.purchasePrice,
    purchaseDate: ts(t.purchaseDate ?? null),
    purchaseOdometer: t.purchaseOdometer,
    initialTreadDepth: t.initialTreadDepth,
    currentTreadDepth: t.currentTreadDepth,
    mountLog: (t.mountLog ?? []).map(serMount),
    spendingId: t.spendingId,
    countPriceInSpend: t.countPriceInSpend ?? false,
    notes: t.notes,
    createdAt: ts(t.createdAt ?? null),
  });
}

function serTrip(t: Trip): Json {
  return clean({
    id: t.id,
    carId: t.carId,
    date: ts(t.date ?? null),
    startOdometer: t.startOdometer,
    endOdometer: t.endOdometer,
    notes: t.notes,
    startLocationName: t.startLocationName,
    endLocationName: t.endLocationName,
    fuelVolume: t.fuelVolume,
    fuelEnergy: t.fuelEnergy,
    fuelCost: t.fuelCost,
    fuelStation: t.fuelStation,
    tollCost: t.tollCost,
    parkingCost: t.parkingCost,
    passengers: t.passengers,
    startLatitude: t.startLatitude,
    startLongitude: t.startLongitude,
    endLatitude: t.endLatitude,
    endLongitude: t.endLongitude,
    waypoints: t.waypoints ?? [],
    polyline: t.polyline ?? [],
    purpose: t.purpose,
    mileageRate: t.mileageRate,
    fuelLogIds: t.fuelLogIds ?? [],
    avgConsumptionOverride: t.avgConsumptionOverride,
    durationSec: t.durationSec,
    avgSpeedKmh: t.avgSpeedKmh,
    topSpeedKmh: t.topSpeedKmh,
  });
}

function serReminder(m: MaintenanceReminder): Json {
  return clean({
    id: m.id,
    carId: m.carId,
    title: m.title,
    notes: m.notes,
    reminderType: m.reminderType ?? "custom",
    dueDate: ts(m.dueDate ?? null),
    dueOdometer: m.dueOdometer,
    notifyBeforeDays: m.notifyBeforeDays,
    notifyBeforeDistance: m.notifyBeforeDistance,
    isCompleted: m.isCompleted ?? false,
    recursEveryDays: m.recursEveryDays,
    recursEveryDistance: m.recursEveryDistance,
    createdAt: ts(m.createdAt ?? null),
  });
}

function serService(s: PreferredService): Json {
  return clean({
    id: s.id,
    name: s.name,
    category: s.category,
    address: s.address,
    phone: s.phone,
    email: s.email,
    notes: s.notes,
    createdAt: ts(s.createdAt ?? null),
  });
}
